/**
 * Coach Engine — Progress Scope
 *
 * Cross-session analyzer. Unlike session scope (which looks inside one
 * group of trades), progress scope looks ACROSS sessions and asks:
 *
 *   - Which patterns are getting worse? (trending up in count)
 *   - Which patterns are getting better? (trending down in count)
 *   - Which patterns look resolved? (haven't fired in a long time)
 *   - Which playbooks are my biggest leaks? (most warnings concentrated)
 *   - Which playbooks are my strongest edge? (most successes concentrated)
 *
 * Emits Observation objects with `scope: 'progress'`. The Report Card UI
 * (Phase 2B-5) will render these alongside sparklines. For now they're
 * consumable by the plain narrator like any other observation.
 *
 * Inputs come from the Observation Archive (2B-3) — this scope is a
 * composition layer, not a data layer.
 */

import {
  flattenObservations,
  queryObservations,
  recurrenceAll,
  countByPlaybook,
  countSessions
} from '../archive';
import { SEVERITY, CATEGORY, SCOPE, makeObservation } from '../types';
import { getCurrentTradingDay, addDaysToDateStr } from '../../timeService';
import * as progressBarPatterns from '../analyzers/progress/progressBarPatterns';
import * as progressHtfPatterns from '../analyzers/progress/progressHtfPatterns';
import * as lossSizeRatio from '../analyzers/progress/lossSizeRatio';
import * as beRuleImpact from '../analyzers/progress/beRuleImpact';
import * as entryDisciplineCalibration from '../analyzers/progress/entryDisciplineCalibration';
import * as setupPatternCalibration from '../analyzers/progress/setupPatternCalibration';
import * as setupComboCalibration from '../analyzers/progress/setupComboCalibration';

// ────────────────────────────────────────────────────────────────────────────
// Tuning knobs — raise these if the coach feels noisy, lower if it feels
// quiet. All are easily testable against real data.
// ────────────────────────────────────────────────────────────────────────────

const MIN_SAMPLE = 2;              // Minimum hits in either window to emit a trend
const MIN_FLAT_WINDOW_HITS = 3;    // Min hits to emit a "recurring theme" observation
const MIN_RESOLVED_HISTORY = 3;    // Min total lifetime hits to call something "resolved"
const RESOLVED_MULTIPLIER = 1.5;   // daysSinceLast > window * this → resolved candidate
const MIN_PLAYBOOK_LEAK = 3;       // Min flags in one playbook to call it out
const PLAYBOOK_DOMINANCE = 1.5;    // Worst playbook must exceed 2nd by this factor

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Modal severity for an analyzer across the entire corpus. Some analyzers
 * only ever fire warnings (e.g. revengeSizing), others are mixed (e.g.
 * thesisValidation). The "typical" severity tells us how to interpret
 * a rising count — bad signal or good signal.
 */
const typicalSeverity = (observations, analyzerKey) => {
  const counts = { success: 0, info: 0, warning: 0, critical: 0 };
  observations.forEach(o => {
    if (o.analyzer !== analyzerKey) return;
    if (counts[o.severity] !== undefined) counts[o.severity] += 1;
  });
  let best = SEVERITY.INFO;
  let bestCount = 0;
  Object.entries(counts).forEach(([sev, n]) => {
    if (n > bestCount) { best = sev; bestCount = n; }
  });
  return best;
};

/**
 * Modal category for an analyzer. Rolls up to the progress observation so
 * downstream UI filters (Risk / Discipline / Variance / …) still work.
 */
const typicalCategory = (observations, analyzerKey) => {
  const counts = {};
  observations.forEach(o => {
    if (o.analyzer !== analyzerKey) return;
    counts[o.category] = (counts[o.category] || 0) + 1;
  });
  let best = CATEGORY.PROCESS;
  let bestCount = 0;
  Object.entries(counts).forEach(([cat, n]) => {
    if (n > bestCount) { best = cat; bestCount = n; }
  });
  return best;
};

/**
 * Best-effort human label for an analyzer. Uses the first observation's
 * title shorn of numbers/colons so "Revenge Sizing: 3 trades within 4m" →
 * "Revenge Sizing". Falls back to humanized camelCase.
 */
const labelFor = (observations, analyzerKey) => {
  const sample = observations.find(o => o.analyzer === analyzerKey);
  if (sample?.title) {
    // Split on em-dash, colon, or hyphen-with-spaces; keep the leading phrase.
    return sample.title.split(/[:—]|\s[-–]\s/)[0].trim();
  }
  return analyzerKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();
};

/** Sample-size → confidence. Caps at 0.9 so the narrator never sounds cocky. */
const confidenceFor = (nSamples) => Math.max(0.4, Math.min(0.9, 0.4 + nSamples * 0.04));

/** Pluralize a flag count for readable copy. */
const flagWord = (n) => `${n} flag${n === 1 ? '' : 's'}`;

// ────────────────────────────────────────────────────────────────────────────
// Per-analyzer trend emitter
// ────────────────────────────────────────────────────────────────────────────

/**
 * Emit up to one observation per analyzer based on its recurrence info.
 * The mapping:
 *
 *   warning/critical + trend 'up'           → WARNING/CRITICAL "getting worse"
 *   warning/critical + trend 'down'         → SUCCESS          "improving"
 *   warning/critical + trend 'flat' + ≥3    → WARNING          "recurring theme"
 *   warning/critical + windowCount=0 + old  → SUCCESS          "looks resolved"
 *   success          + trend 'up'           → SUCCESS          "more frequent"
 *   else                                     → skip             (too quiet or ambiguous)
 */
const emitAnalyzerTrend = ({ all, analyzer, info, windowDays }) => {
  if (info.windowCount < MIN_SAMPLE && info.previousWindowCount < MIN_SAMPLE) {
    // Special case: resolved candidate might be worth emitting even with
    // low recent counts (because the recent count is zero and prior is high).
    const resolvedCutoff = Math.round(windowDays * RESOLVED_MULTIPLIER);
    const isResolvedCandidate =
      info.windowCount === 0 &&
      info.total >= MIN_RESOLVED_HISTORY &&
      info.daysSinceLast !== null &&
      info.daysSinceLast >= resolvedCutoff;
    if (!isResolvedCandidate) return null;
  }

  const sev   = typicalSeverity(all, analyzer);
  const cat   = typicalCategory(all, analyzer);
  const label = labelFor(all, analyzer);
  const resolvedCutoff = Math.round(windowDays * RESOLVED_MULTIPLIER);

  // ── Resolved: prior pattern that hasn't surfaced in a long while ──
  if (
    info.windowCount === 0 &&
    info.total >= MIN_RESOLVED_HISTORY &&
    info.daysSinceLast !== null &&
    info.daysSinceLast >= resolvedCutoff
  ) {
    return makeObservation({
      id: `progress:${analyzer}:${windowDays}d:resolved`,
      analyzer: 'progress.trend',
      scope: SCOPE.PROGRESS,
      scopeId: `${windowDays}d`,
      severity: SEVERITY.SUCCESS,
      category: cat,
      title: `${label} — looks resolved`,
      detail: `Last flagged ${info.daysSinceLast} days ago after ${info.total} total occurrences. No hits in the last ${windowDays} days.`,
      action: `Keep doing whatever you changed — it's working.`,
      evidence: { analyzer, info },
      metric: { label: 'Days since last', value: info.daysSinceLast, unit: 'days' },
      confidence: confidenceFor(info.total),
      priority: 65
    });
  }

  // ── Warning/critical analyzers: rising count is BAD ──
  if (sev === SEVERITY.WARNING || sev === SEVERITY.CRITICAL) {
    if (info.trend === 'up') {
      const delta = info.windowCount - info.previousWindowCount;
      const ratio = info.previousWindowCount > 0
        ? (info.windowCount / info.previousWindowCount).toFixed(1)
        : null;
      return makeObservation({
        id: `progress:${analyzer}:${windowDays}d:up`,
        analyzer: 'progress.trend',
        scope: SCOPE.PROGRESS,
        scopeId: `${windowDays}d`,
        severity: sev === SEVERITY.CRITICAL ? SEVERITY.CRITICAL : SEVERITY.WARNING,
        category: cat,
        title: `${label} trending up`,
        detail: ratio
          ? `${flagWord(info.windowCount)} in the last ${windowDays}d vs ${info.previousWindowCount} in the prior ${windowDays}d (${ratio}×).`
          : `${flagWord(info.windowCount)} in the last ${windowDays}d — up from ${info.previousWindowCount} in the prior window.`,
        action: `Review the last ${info.windowCount} occurrence${info.windowCount === 1 ? '' : 's'} for a common trigger. Rule change may be needed.`,
        evidence: { analyzer, info, delta },
        metric: { label: 'Recent flags', value: info.windowCount, baseline: info.previousWindowCount, unit: 'count' },
        confidence: confidenceFor(info.windowCount + info.previousWindowCount),
        priority: sev === SEVERITY.CRITICAL ? 85 : 75
      });
    }

    if (info.trend === 'down' && info.previousWindowCount >= MIN_SAMPLE) {
      const delta = info.previousWindowCount - info.windowCount;
      return makeObservation({
        id: `progress:${analyzer}:${windowDays}d:down`,
        analyzer: 'progress.trend',
        scope: SCOPE.PROGRESS,
        scopeId: `${windowDays}d`,
        severity: SEVERITY.SUCCESS,
        category: cat,
        title: `${label} trending down`,
        detail: `${flagWord(info.windowCount)} in the last ${windowDays}d vs ${info.previousWindowCount} in the prior window — ${delta} fewer.`,
        action: `Whatever you changed is working. Lock it in — write it into Lessons on a recent session.`,
        evidence: { analyzer, info, delta },
        metric: { label: 'Recent flags', value: info.windowCount, baseline: info.previousWindowCount, unit: 'count' },
        confidence: confidenceFor(info.windowCount + info.previousWindowCount),
        priority: 60
      });
    }

    if (info.trend === 'flat' && info.windowCount >= MIN_FLAT_WINDOW_HITS) {
      return makeObservation({
        id: `progress:${analyzer}:${windowDays}d:flat`,
        analyzer: 'progress.trend',
        scope: SCOPE.PROGRESS,
        scopeId: `${windowDays}d`,
        severity: SEVERITY.WARNING,
        category: cat,
        title: `${label} is a recurring theme`,
        detail: `${flagWord(info.windowCount)} in the last ${windowDays}d, similar to ${info.previousWindowCount} in the prior window — no movement.`,
        action: `Effort alone isn't moving this. The fix is probably a rule change, not more willpower.`,
        evidence: { analyzer, info },
        metric: { label: 'Recent flags', value: info.windowCount, unit: 'count' },
        confidence: confidenceFor(info.windowCount),
        priority: 70
      });
    }
  }

  // ── Success analyzers: rising count is GOOD ──
  if (sev === SEVERITY.SUCCESS && info.trend === 'up') {
    return makeObservation({
      id: `progress:${analyzer}:${windowDays}d:up-success`,
      analyzer: 'progress.trend',
      scope: SCOPE.PROGRESS,
      scopeId: `${windowDays}d`,
      severity: SEVERITY.SUCCESS,
      category: cat,
      title: `${label} more frequent`,
      detail: `${info.windowCount} positive signals in the last ${windowDays}d vs ${info.previousWindowCount} in the prior window.`,
      action: `Nice. What changed? Lock it into your pre-trade checklist.`,
      evidence: { analyzer, info },
      metric: { label: 'Successes', value: info.windowCount, baseline: info.previousWindowCount, unit: 'count' },
      confidence: confidenceFor(info.windowCount + info.previousWindowCount),
      priority: 55
    });
  }

  return null;
};

// ────────────────────────────────────────────────────────────────────────────
// Playbook-scope observations
// ────────────────────────────────────────────────────────────────────────────

/**
 * Identify the playbook with the heaviest warning/critical concentration.
 * Only fires if one playbook dominates — we don't want to flag "everything
 * leaks equally."
 */
const emitPlaybookLeak = ({ sessions, playbooks, windowDays, anchor }) => {
  const windowWarnings = queryObservations({
    sessions, playbooks, windowDays, windowAnchor: anchor
  }).filter(o => o.severity === SEVERITY.WARNING || o.severity === SEVERITY.CRITICAL);

  const rows = countByPlaybook(windowWarnings);
  if (rows.length === 0) return null;
  const worst = rows[0];
  if (worst.playbookId === 'none') return null;   // skip unassigned bucket
  const next = rows[1]?.count || 0;
  if (worst.count < MIN_PLAYBOOK_LEAK) return null;
  if (worst.count < next * PLAYBOOK_DOMINANCE) return null;

  return makeObservation({
    id: `progress:playbook:${worst.playbookId}:${windowDays}d:leak`,
    analyzer: 'progress.playbook',
    scope: SCOPE.PROGRESS,
    scopeId: `${windowDays}d`,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `Biggest leak: ${worst.playbookName || 'Unnamed playbook'}`,
    detail: `${worst.count} warning/critical flags in the last ${windowDays}d — more than any other playbook.`,
    action: `Audit the rules for this playbook specifically. What's the weakest link in the setup?`,
    evidence: { playbookId: worst.playbookId, rows },
    metric: { label: 'Flags in playbook', value: worst.count, unit: 'count' },
    confidence: confidenceFor(worst.count),
    priority: 78
  });
};

/**
 * Identify the playbook with the strongest positive concentration. Same
 * dominance rule as the leak detector so we don't emit noise.
 */
const emitPlaybookStrong = ({ sessions, playbooks, windowDays, anchor }) => {
  const windowWins = queryObservations({
    sessions, playbooks, windowDays, windowAnchor: anchor, severity: SEVERITY.SUCCESS
  });

  const rows = countByPlaybook(windowWins);
  if (rows.length === 0) return null;
  const best = rows[0];
  if (best.playbookId === 'none') return null;
  const next = rows[1]?.count || 0;
  if (best.count < MIN_PLAYBOOK_LEAK) return null;
  if (best.count < next * PLAYBOOK_DOMINANCE) return null;

  return makeObservation({
    id: `progress:playbook:${best.playbookId}:${windowDays}d:strong`,
    analyzer: 'progress.playbook',
    scope: SCOPE.PROGRESS,
    scopeId: `${windowDays}d`,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.PROCESS,
    title: `Your most consistent strategy: ${best.playbookName || 'Unnamed playbook'}`,
    detail: `${best.count} positive signals in the last ${windowDays}d — more than any other playbook.`,
    action: `This is your edge. Can you run it more often, or on more accounts?`,
    evidence: { playbookId: best.playbookId, rows },
    metric: { label: 'Positive signals', value: best.count, unit: 'count' },
    confidence: confidenceFor(best.count),
    priority: 68
  });
};

// ────────────────────────────────────────────────────────────────────────────
// Overall discipline mix
// ────────────────────────────────────────────────────────────────────────────

/**
 * Top-level "how are you trending?" observation. Compares warning+critical
 * load in the current window vs the prior equal-length window. Emits only
 * if the delta is material (≥20% change) to avoid noise.
 */
const emitDisciplineDelta = ({ sessions, playbooks, windowDays, anchor }) => {
  const all = flattenObservations(sessions, { playbooks });
  if (all.length === 0) return null;

  const cur = queryObservations({
    sessions, playbooks, windowDays, windowAnchor: anchor
  });

  // Prior window: [anchor - 2w, anchor - w] exclusive of current.
  // String-based arithmetic via timeService — no UTC drift, anchor honors
  // the trading-day rollover when callers omit it.
  const anchorStr = anchor || getCurrentTradingDay();
  const prevEnd   = addDaysToDateStr(anchorStr, -windowDays);
  const prevStart = addDaysToDateStr(anchorStr, -(windowDays * 2 - 1));
  const prev = queryObservations({
    sessions, playbooks, after: prevStart, before: prevEnd
  });

  const curWarn  = cur.filter(o => o.severity === SEVERITY.WARNING || o.severity === SEVERITY.CRITICAL).length;
  const prevWarn = prev.filter(o => o.severity === SEVERITY.WARNING || o.severity === SEVERITY.CRITICAL).length;
  if (curWarn === 0 && prevWarn === 0) return null;

  // Both sides use bounded counts so prevSessions is the count for the
  // PRIOR window only — not all-time. The previous implementation had
  // `prevSessions = countSessions(sessions, { windowDays: undefined })`
  // which returned all-time totals, making the evidence object misleading
  // (the detail string only quoted curSessions, masking the bug).
  const curSessions  = countSessions(sessions, { windowDays, windowAnchor: anchor });
  const prevSessions = countSessions(sessions, { after: prevStart, before: prevEnd });

  // Require ≥20% delta to avoid noise from small sample fluctuation
  const denom = Math.max(prevWarn, 1);
  const relDelta = (curWarn - prevWarn) / denom;
  if (Math.abs(relDelta) < 0.2) return null;

  const improving = relDelta < 0;
  const pct = Math.round(Math.abs(relDelta) * 100);

  return makeObservation({
    id: `progress:discipline:${windowDays}d:${improving ? 'up' : 'down'}`,
    analyzer: 'progress.discipline',
    scope: SCOPE.PROGRESS,
    scopeId: `${windowDays}d`,
    severity: improving ? SEVERITY.SUCCESS : SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: improving
      ? `Discipline improving`
      : `Discipline slipping`,
    detail: improving
      ? `Warning/critical flags down ${pct}% vs the prior ${windowDays}d (${curWarn} vs ${prevWarn}) across ${curSessions} session${curSessions === 1 ? '' : 's'}.`
      : `Warning/critical flags up ${pct}% vs the prior ${windowDays}d (${curWarn} vs ${prevWarn}) across ${curSessions} session${curSessions === 1 ? '' : 's'}.`,
    action: improving
      ? `Whatever the last few sessions changed — keep it.`
      : `Something drifted. Look at the last 3–5 sessions for the common thread.`,
    evidence: { curWarn, prevWarn, curSessions, prevSessions },
    metric: { label: 'Warning flags', value: curWarn, baseline: prevWarn, unit: 'count' },
    confidence: confidenceFor(curWarn + prevWarn),
    priority: improving ? 72 : 82
  });
};

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

/**
 * Produce progress observations across all sessions.
 *
 * @param {Object} ctx
 * @param {Array}  ctx.sessions
 * @param {Array}  [ctx.playbooks]
 * @param {string} [ctx.currentTradingDay]  'YYYY-MM-DD' — anchors windows to
 *                                           the futures trading-day boundary
 * @param {number} [ctx.windowDays=30]
 * @returns {Observation[]}
 */
export const analyzeProgress = ({
  sessions = [],
  playbooks = [],
  currentTradingDay,
  windowDays = 30,
  // Optional — when supplied, enables cross-day bar-context patterns
  // (sharp-entry hour clustering, MFE-leakage hour clustering, etc.).
  // Trades need `barAnalysis` populated; the recently-shipped backfill
  // (Settings → Charts → Run backfill) is the enabling step.
  allTrades = null
} = {}) => {
  const anchor = currentTradingDay || undefined;
  const out = [];

  // Sections 1-3 require sessions + observation history. The bar-pattern
  // section (4) only needs trades, so we run it independently — a fresh
  // user with no sessions but populated bar context still gets cross-day
  // observations.
  const hasSessions = Array.isArray(sessions) && sessions.length > 0;
  const all = hasSessions ? flattenObservations(sessions, { playbooks }) : [];

  if (all.length > 0) {
    // 1) Per-analyzer trends
    const rows = recurrenceAll(all, { windowDays, anchor });
    rows.forEach(({ analyzer, info }) => {
      const obs = emitAnalyzerTrend({ all, analyzer, info, windowDays });
      if (obs) out.push(obs);
    });

    // 2) Playbook standouts
    const leak = emitPlaybookLeak({ sessions, playbooks, windowDays, anchor });
    if (leak) out.push(leak);
    const strong = emitPlaybookStrong({ sessions, playbooks, windowDays, anchor });
    if (strong) out.push(strong);

    // 3) Overall discipline delta
    const discipline = emitDisciplineDelta({ sessions, playbooks, windowDays, anchor });
    if (discipline) out.push(discipline);
  }

  // 4) Cross-day bar-context patterns. Only fires when allTrades is provided
  // AND enough trades have barAnalysis populated.
  if (Array.isArray(allTrades) && allTrades.length > 0) {
    try {
      const barObs = progressBarPatterns.analyze({
        allTrades,
        currentTradingDay: anchor,
        windowDays
      });
      if (Array.isArray(barObs)) out.push(...barObs);
    } catch (e) {
      // Don't let analyzer failures kill the whole progress run.
      // eslint-disable-next-line no-console
      console.warn('[Coach] progressBarPatterns failed:', e?.message || e);
    }

    // 4b) Cross-day HTF patterns. Surfaces "your against-4h rate is climbing"
    // and "HTF-aligned trades outperform by X pp" — needs barAnalysis.htfEmas
    // populated, which the modal + backfill now do automatically.
    try {
      const htfObs = progressHtfPatterns.analyze({
        allTrades,
        currentTradingDay: anchor,
        windowDays
      });
      if (Array.isArray(htfObs)) out.push(...htfObs);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Coach] progressHtfPatterns failed:', e?.message || e);
    }

    // 4c) Wave A — Hougaard loss-size ratio. 30D rolling |avg loss| / avg win.
    // Single observation per run; bands SUCCESS / WARNING / CRITICAL.
    try {
      const ratioObs = lossSizeRatio.analyze({
        allTrades,
        currentTradingDay: anchor,
        windowDays,
      });
      if (ratioObs) out.push(ratioObs);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Coach] lossSizeRatio failed:', e?.message || e);
    }

    // 4d) Wave A — BE-rule impact (measure-only). Compares trader's own
    // outcomes between "moved to BE" vs "held without BE" cohorts.
    // Fires when delta in conserved-winner rate > 15% to settle the
    // Diaz-vs-Rake debate empirically per-user.
    try {
      const beObs = beRuleImpact.analyze({
        allTrades,
        currentTradingDay: anchor,
        windowDays: 60,  // BE rule needs more samples than ratio analyzer
      });
      if (beObs) out.push(beObs);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Coach] beRuleImpact failed:', e?.message || e);
    }

    // 4e) Wave B.2 — entry discipline calibration. Stalker vs Chaser WR
    // comparison. Educator literature is unanimous that stalker wins;
    // this surfaces the trader's own data either way.
    try {
      const discObs = entryDisciplineCalibration.analyze({
        allTrades,
        currentTradingDay: anchor,
        windowDays,
      });
      if (discObs) out.push(discObs);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Coach] entryDisciplineCalibration failed:', e?.message || e);
    }

    // 4f) Wave C — setup pattern calibration. For each of the 7 mechanical
    // patterns, compare trades-with-pattern vs trades-without. Surfaces
    // which patterns are actually working for THIS trader (and which the
    // literature claims work but don't for them).
    try {
      const patternObs = setupPatternCalibration.analyze({
        allTrades,
        currentTradingDay: anchor,
        windowDays,
      });
      if (Array.isArray(patternObs)) out.push(...patternObs);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Coach] setupPatternCalibration failed:', e?.message || e);
    }

    // 4g) Wave G.1 — pair-wise pattern co-occurrence calibration. Where 4f
    // asks "does pattern X outperform?", this asks "do patterns X + Y together
    // outperform either one alone OR the baseline?" The stacked-signal answer.
    try {
      const comboObs = setupComboCalibration.analyze({
        allTrades,
        currentTradingDay: anchor,
        windowDays,
      });
      if (Array.isArray(comboObs)) out.push(...comboObs);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Coach] setupComboCalibration failed:', e?.message || e);
    }
  }

  // Sort by priority desc so callers can take top-N for compact surfaces.
  out.sort((a, b) => b.priority - a.priority);
  return out;
};

export default { analyzeProgress };
